import DotIcon from "@/assets/colorable/dot.svg?react";
import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import RightChevronIcon from "@/assets/colorable/right_chevron.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import CompanyLogoPlaceholder from "@/assets/images/company_logo_placeholder.svg";
import Button from "@/components/Common/Button";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import {packages} from "@/hooks/usePackages";
import {useIsAgency, useIsCompany, useIsInfuencer, useIsSubbrand} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import type {TPackageName} from "@/store/types";
import useUserStore from "@/store/user";
import {useNavigate} from "@tanstack/react-router";
import findKey from "lodash/findKey";
import type {FC} from "react";
import {useTranslation} from "react-i18next";

const SidebarProfileTrigger: FC<{
  hasFailedPayment?: boolean;
  hasIncompleteProfile?: boolean;
  imageUrl?: string | null;
  isCollapsed: boolean;
  isOpen?: boolean;
  name?: string | null;
  onClick?(): void;
  onFailedPaymentClick?(): void;
}> = ({hasFailedPayment, hasIncompleteProfile, imageUrl, isCollapsed, isOpen, name, onClick, onFailedPaymentClick}) => {
  const {t} = useTranslation(["common", "navigation"]);
  const navigate = useNavigate();
  const isAgency = useIsAgency();
  const isCompany = useIsCompany();
  const isInfluencer = useIsInfuencer();
  const isSubbrand = useIsSubbrand();
  const isUserLoaded = useUserStore(state => !!state.user.id);
  const subscription = useUserStore(state => state.user.package_subscription);
  const showTwoWeeksTrial = useUserStore(
    state => !state.user.package_subscription && !state.user.has_past_package_subscription,
  );
  const plan = findKey(packages, {type: subscription?.name}) as TPackageName | undefined;

  const isInfluencerUser = isInfluencer && !(isCompany || isSubbrand || isAgency);
  const showJoinNow = isUserLoaded && !isInfluencerUser && !plan;

  const isDangerAttention = showJoinNow || !!hasFailedPayment;
  const needsAttention = isDangerAttention || !!hasIncompleteProfile;
  const attentionLabel = hasFailedPayment
    ? t("common:payment_failed")
    : showJoinNow
      ? t("navigation:join_now")
      : hasIncompleteProfile
        ? t("navigation:profile_incomplete")
        : null;

  return (
    <div className="bg-primary-powder flex flex-col gap-2 overflow-hidden p-3 whitespace-nowrap">
      <button
        onClick={onClick}
        className={clsxm(
          "flex w-full items-center rounded-lg transition-colors",
          isCollapsed ? "justify-center" : "gap-1.5",
        )}>
        <span className="relative shrink-0">
          <img
            alt=""
            className="border-border size-6 rounded-md border object-cover"
            src={imageUrl ?? CompanyLogoPlaceholder}
          />
          {needsAttention && (
            <>
              <span
                aria-hidden
                className={clsxm(
                  "absolute -top-0.5 -right-0.5 size-2 rounded-full border border-white",
                  isDangerAttention ? "bg-danger-rubicund" : "bg-warning-mustard",
                )}
              />
              {isCollapsed && <span className="sr-only">{attentionLabel}</span>}
            </>
          )}
        </span>
        {!isCollapsed && (
          <>
            <span className="flex min-w-0 grow flex-col items-start gap-1">
              <span className="text-blue-medium-base text-2xs w-full truncate text-left leading-none font-semibold">
                {name}
              </span>
              {!isUserLoaded ? (
                <SkeletonLoader className="h-2 min-h-0 w-20" />
              ) : plan ? (
                <span className="text-blue-medium-base text-2xs flex items-center gap-1 leading-none">
                  {t(packages[plan].translationKey as "packageAdvanced")}
                  <DotIcon className="size-1 shrink-0" />
                  {subscription?.instagram_campaigns && <InstagramIcon className="h-2 w-auto shrink-0" />}
                  {subscription?.instagram_campaigns && subscription?.tiktok_campaigns && "+"}
                  {subscription?.tiktok_campaigns && <TiktokIcon className="h-2 w-auto shrink-0" />}
                </span>
              ) : (
                showJoinNow && (
                  <span
                    className="bg-primary-powder hover:bg-muted text-primary-dark-solid text-2xs flex min-h-4 max-w-full cursor-pointer flex-wrap items-center gap-x-4 gap-y-1 overflow-hidden rounded px-1 py-0.5 leading-none"
                    onClick={event => {
                      event.stopPropagation();
                      navigate({to: "/plans"});
                    }}>
                    {t("navigation:join_now")}
                    {showTwoWeeksTrial && (
                      <span className="relative">
                        <DotIcon className="absolute top-1/2 -left-2.5 size-1 -translate-y-1/2" />
                        {t("navigation:two_week_trial")}
                      </span>
                    )}
                  </span>
                )
              )}
            </span>
            <RightChevronIcon
              className={clsxm(
                "text-blue-medium-base size-2.5 shrink-0 transition-transform",
                isOpen ? "-rotate-90" : "rotate-90",
              )}
            />
          </>
        )}
      </button>
      {hasFailedPayment && !isCollapsed && (
        <Button
          buttonStyle="Custom"
          className="bg-danger-light-red hover:bg-danger-rubicund/30 text-neutral-black text-2xs h-auto min-h-6 w-fit rounded-full px-2 py-0 font-semibold tracking-wider uppercase"
          onClick={onFailedPaymentClick}
          shouldScaleOnHover={false}>
          {t("common:payment_failed")}
        </Button>
      )}
    </div>
  );
};

export default SidebarProfileTrigger;
